import type { Annotations, PlotData } from "plotly.js";

/**
 * Etichette "fumetto" per la stampa.
 *
 * A video il valore di un punto del grafico si legge passando il mouse: Plotly mostra
 * un'etichetta con lo sfondo del colore della linea e il testo di hover della traccia.
 * Sul PDF il mouse non c'e', quindi quell'informazione andava persa. Queste funzioni
 * replicano l'etichetta come annotazione fissa, con lo stesso testo che l'utente vede a
 * video (`hovertext`, altrimenti `text`, altrimenti il valore formattato).
 */

type Trace = Partial<PlotData>;

export type PrintLabelPoint = "last" | "min";

export interface PrintLabelTarget {
  trace: Trace | null | undefined;
  /** In quale punto della serie mettere l'etichetta. */
  at: PrintLabelPoint;
}

const currencyFormatter = new Intl.NumberFormat("it-IT", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Distanza minima fra i centri di due etichette dello stesso gruppo, in pixel. */
const LABEL_STEP_PX = 22;
/** Altezza dell'area del grafico, se il chiamante non la indica. */
const DEFAULT_PLOT_HEIGHT_PX = 300;

/** La traccia e' disegnata? `legendonly` e `false` sono serie spente dalla legenda. */
export function isTraceVisible(trace: Trace | null | undefined): boolean {
  if (trace == null) {
    return false;
  }
  return trace.visible !== false && trace.visible !== "legendonly";
}

function traceColor(trace: Trace): string {
  const color = (trace.line as any)?.color ?? (trace.marker as any)?.color ?? "#394b59";
  return typeof color === "string" ? color : "#394b59";
}

function pointText(trace: Trace, index: number, y: number): string {
  const hovertext = trace.hovertext;
  if (Array.isArray(hovertext) && hovertext[index] != null) {
    return String(hovertext[index]).trim();
  }
  if (typeof hovertext === "string" && hovertext.length > 0) {
    return hovertext.trim();
  }
  const text = trace.text;
  if (Array.isArray(text) && text[index] != null) {
    return String(text[index]).trim();
  }
  return currencyFormatter.format(y);
}

function pointIndex(ys: number[], at: PrintLabelPoint): number {
  if (at === "last") {
    for (let i = ys.length - 1; i >= 0; i--) {
      if (Number.isFinite(ys[i])) {
        return i;
      }
    }
    return -1;
  }
  let best = -1;
  for (let i = 0; i < ys.length; i++) {
    if (Number.isFinite(ys[i]) && (best < 0 || ys[i] < ys[best])) {
      best = i;
    }
  }
  return best;
}

/**
 * Costruisce le annotazioni da aggiungere a `layout.annotations` in stampa.
 *
 * Le etichette all'ultimo punto stanno a sinistra del punto (il grafico finisce li'),
 * quelle al minimo a destra. Etichette che cadrebbero una sull'altra vengono
 * distanziate in verticale, mantenendo la freccia sul punto esatto.
 */
export function buildPrintHoverLabels(
  targets: PrintLabelTarget[],
  options?: {
    logY?: boolean;
    /**
     * Tutte le tracce del grafico: servono a stimare l'escursione reale dell'asse Y
     * (che puo' includere serie non etichettate, es. un conto deposito che scende a 0)
     * e quindi la distanza in pixel fra due etichette.
     */
    allTraces?: (Trace | null | undefined)[];
    /** Altezza in pixel dell'area del grafico (senza margini). */
    plotHeight?: number;
  }
): Partial<Annotations>[] {
  type Label = { x: any; y: number; text: string; color: string; at: PrintLabelPoint };
  const labels: Label[] = [];
  let yMin = Infinity;
  let yMax = -Infinity;

  const extentOf = (trace: Trace | null | undefined) => {
    if (!isTraceVisible(trace) || trace == null) {
      return;
    }
    ((trace.y ?? []) as any[]).forEach((raw) => {
      const v = Number(raw);
      if (Number.isFinite(v)) {
        yMin = Math.min(yMin, v);
        yMax = Math.max(yMax, v);
      }
    });
  };
  (options?.allTraces ?? targets.map((t) => t.trace)).forEach(extentOf);

  targets.forEach(({ trace, at }) => {
    if (!isTraceVisible(trace) || trace == null) {
      return;
    }
    const xs = (trace.x ?? []) as any[];
    const ys = ((trace.y ?? []) as any[]).map((v) => Number(v));
    const index = pointIndex(ys, at);
    if (index < 0 || xs[index] == null) {
      return;
    }
    labels.push({
      x: xs[index],
      y: ys[index],
      text: pointText(trace, index, ys[index]),
      color: traceColor(trace),
      at,
    });
  });

  const range = yMax > yMin ? yMax - yMin : 1;
  const plotHeight = options?.plotHeight ?? DEFAULT_PLOT_HEIGHT_PX;
  // Posizione verticale stimata in pixel (dall'alto) del punto etichettato.
  const toPx = (y: number) => {
    if (options?.logY === true && y > 0 && yMin > 0) {
      const lo = Math.log10(yMin);
      const hi = Math.log10(yMax);
      return hi > lo ? ((hi - Math.log10(y)) / (hi - lo)) * plotHeight : 0;
    }
    return ((yMax - y) / range) * plotHeight;
  };

  // Distanziamento: etichette dello stesso gruppo (fine / minimo) ordinate dall'alto
  // verso il basso; ognuna scende quanto basta a stare almeno LABEL_STEP_PX sotto la
  // precedente. La freccia resta sul punto esatto.
  const offsets = new Map<Label, number>();
  (["last", "min"] as PrintLabelPoint[]).forEach((at) => {
    const group = labels.filter((l) => l.at === at).sort((a, b) => b.y - a.y);
    let previousCenter = -Infinity;
    group.forEach((label) => {
      const pointPx = toPx(label.y);
      const center = Math.max(pointPx, previousCenter + LABEL_STEP_PX);
      offsets.set(label, center - pointPx);
      previousCenter = center;
    });
  });

  return labels.map((label) => ({
    x: label.x,
    y: options?.logY === true && label.y > 0 ? Math.log10(label.y) : label.y,
    xref: "x",
    yref: "y",
    text: label.text,
    showarrow: true,
    arrowhead: 0,
    arrowwidth: 1.5,
    arrowcolor: label.color,
    ax: label.at === "last" ? -14 : 14,
    ay: offsets.get(label) ?? 0,
    xanchor: label.at === "last" ? "right" : "left",
    yanchor: "middle",
    align: "left",
    bgcolor: label.color,
    bordercolor: label.color,
    borderpad: 3,
    font: { color: "#ffffff", size: 11 },
  }));
}
